import { defineStore } from "pinia";
import router from "@app/router";
import { commClient } from "fw4-library";
import { i18n } from "@app/i18n";

export interface IKeyValue {
    Key: number | string;
    Value: number | string;
    AdditionalData?: Record<string, any>
}

export interface ILoginForm {
    Msisdn: number | null;
    Username: string | null;
    Password: string;
    ValidationCode: string;
}

export interface IRegistrationForm {
    Firstname: string;
    Surname: string;
    TaxNumber: number | null;
    Birthdate: Date | null;
    Prefix: IKeyValue | undefined;
    Msisdn: number | null;
    Email: string;
    Password: string;
    RepeatPassword: string;
    ValidationCode: string;
    GDPR: boolean;
}

export interface IPasswordResetForm {
    Prefix: IKeyValue | undefined;
    Msisdn: number | null;
    TaxNumber: number | null;
    Email: string,
    NewPassword: string;
    RepeatPassword: string;
    PasswordChangeRequestId: string;
    TRR4Numbers: string;
    ValidationCode: string;
}

export interface IMissingFields {
    Firstname: boolean;
    Surname: boolean;
    Birthdate: boolean;
    TaxNumber: boolean;
}

export interface IProfileCompletionForm {
    Firstname: string;
    Surname: string;
    Birthdate: Date | null;
    TaxNumber: number | null;
}

export interface IPublicHelpers {
    ServerError: string;
    UserType: string;
    CorrectDavcna: boolean;
    DataCheck: boolean;
    CodeSent: boolean;
    UserExists: boolean;
    NextStepDisabled: Boolean;
    MissingFields: IMissingFields;
}

export const LoginInitial: ILoginForm = {
    Msisdn: null,
    Username: null,
    Password: "",
    ValidationCode: "",
}

export const RegistrationInitial: IRegistrationForm = {
    Firstname: "",
    Surname: "",
    TaxNumber: null,
    Birthdate: null,
    Prefix: { Key: "386", Value: "SI +386" },
    Msisdn: null,
    Email: "",
    Password: "",
    RepeatPassword: "",
    ValidationCode: "",
    GDPR: false
}

export const PasswordResetInitial: IPasswordResetForm = {
    Prefix: { Key: "386", Value: "SI +386" },
    Msisdn: null,
    TaxNumber: null,
    Email: "",
    NewPassword: "",
    RepeatPassword: "",
    PasswordChangeRequestId: "",
    TRR4Numbers: "",
    ValidationCode: ""
}

export interface IPeskovnikTestiranje {
    Datum: Date | null;
    Stevilka: number | null;
}

export const ProfileCompletionInitial: IProfileCompletionForm = {
    Firstname: "",
    Surname: "",
    Birthdate: null,
    TaxNumber: null,
}

export interface IPublicState {
    Login: ILoginForm;
    CountryPhoneList: IKeyValue[];
    Registration: IRegistrationForm;
    PasswordReset: IPasswordResetForm;
    ProfileCompletion: IProfileCompletionForm;
    PublicHelpers: IPublicHelpers;
    PeskovnikTestiranje: IPeskovnikTestiranje;
}

interface IPasswordDataCheck {
    status: number;
    PasswordChangeRequestId: string;
}

export const usePublicStore = defineStore('public', {
    state: (): IPublicState => {
        return {
            Login: JSON.parse(JSON.stringify(LoginInitial)),
            PublicHelpers: {
                ServerError: "",
                UserType: "X",
                CorrectDavcna: false,
                DataCheck: false,
                CodeSent: false,
                UserExists: true,
                NextStepDisabled: false,
                MissingFields: {
                    Firstname: false,
                    Surname: false,
                    Birthdate: false,
                    TaxNumber: false,
                }
            },
            CountryPhoneList: [],
            ProfileCompletion: JSON.parse(JSON.stringify(ProfileCompletionInitial)),
            Registration: JSON.parse(JSON.stringify(RegistrationInitial)),
            PasswordReset: JSON.parse(JSON.stringify(PasswordResetInitial)),
            PeskovnikTestiranje:{
                Datum: null,
                Stevilka: null
            }
        }
    },
    actions: {
        // async UsernameCheck(){
        //     this.PublicHelpers.ServerError = '';

        //     const checkComplete = (Message: any): void => {
        //         if (!Message.Success) {
        //             this.PublicHelpers.ServerError = 'USERNAME_NOT_EXISTS';
        //             this.PublicHelpers.UserExists = false;
        //         }
        //         else{
        //             this.PublicHelpers.UserExists = true;
        //         }
        //     };

        //     if(this.Login.Username){
        //         commClient.executeMethod("sp.UsernameCheck", {
        //             Username: this.isUsernameNumber ? this.Login.Username!.replace(/[\s+]+/g, '').trim() : this.Login.Username!.trim()
        //         }, checkComplete);
        //     }
        // },
        async PortalLogin() {
            this.PublicHelpers.ServerError = '';
            
            const loginComplete = (Message: any): void => {
                if (Message.Success) {
                    router.push({ name: "sign_in_two" });
                }else{
                    this.PublicHelpers.ServerError = Message.Error;
                }
            };

            commClient.executeMethod("PortalLogin", {
                PhoneNumber: this.isUsernameNumber ? this.Login.Username!.replace(/[\s+]+/g, '').trim() : null,
                Email: !this.isUsernameNumber ? this.Login.Username!.trim() : null,
                Password: this.Login.Password
            }, loginComplete);
        },

        async userLogOut() {
            const logoutComplete = (Message: any): void => {
            };

            commClient.executeMethod("LogOut",
                {

                },
            logoutComplete);
        },

        async passwordChangeRequest() {
            this.PublicHelpers.ServerError = '';

            const loadComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        if (Message.IsEmailVerified) {
                            router.push({ name: "reset-details" });
                        } else {
                            router.push({ name: "reset-details-trr" });
                        }
                    } else {
                        this.PublicHelpers.ServerError = Message.Error;
                    }
                }
            };

            commClient.executeMethod("PasswordChangeRequest", {
                "PhoneNumber": this.PasswordReset.Prefix?.Key.toString().replace("+", "").trim() + this.PasswordReset.Msisdn!.toString()
            }, loadComplete);
        },

        async checkPin(phoneNumber: string, validationCode: string): Promise<void> {
            this.PublicHelpers.ServerError = '';

            return new Promise<void>((resolve, reject) => {
                const loadComplete = (Message: any): void => {
                    if (Message.Success) {
                        resolve();
                    } else {
                        this.PublicHelpers.ServerError = Message.Error;
                        reject();
                    }
                };

                commClient.executeMethod("PinCodeCheck", {
                    "PhoneNumber": phoneNumber,
                    "Code": validationCode
                }, loadComplete);
            })
        },

        async checkLoginPin() {
            this.PublicHelpers.ServerError = '';

            const loadComplete = (Message: any): void => {
                if (Message.Success) {
                    console.log(JSON.stringify(Message));
                    if (Message.RequiresProfileCompletion && Message.MissingFields) {
                        this.PublicHelpers.MissingFields = Message.MissingFields;
                        this.ProfileCompletion = JSON.parse(JSON.stringify(ProfileCompletionInitial));
                        router.push({ name: "complete-profile" });
                    } else {
                        router.push({ name: "app_home" });
                    }
                } 
                else {
                    this.PublicHelpers.ServerError = Message.Error;
                }
            };

            commClient.executeMethod("PinCodeCheck", {
                PhoneNumber: this.isUsernameNumber ? this.Login.Username!.replace(/[\s+]+/g, '').trim() : null,
                Email: !this.isUsernameNumber ? this.Login.Username!.trim() : null,
                Code: this.Login.ValidationCode,
                Password: this.Login.Password,
            }, loadComplete);
        },

        async completeProfile() {
            this.PublicHelpers.ServerError = '';

            const loadComplete = (Message: any): void => {
                if (Message.Success) {
                    router.push({ name: "app_home" });
                } else {
                    this.PublicHelpers.ServerError = Message.Error;
                }
            };

            commClient.executeMethod("CompleteProfile", {
                Firstname: this.PublicHelpers.MissingFields.Firstname ? this.ProfileCompletion.Firstname : null,
                Surname: this.PublicHelpers.MissingFields.Surname ? this.ProfileCompletion.Surname : null,
                Birthdate: this.PublicHelpers.MissingFields.Birthdate ? this.ProfileCompletion.Birthdate : null,
                TaxNumber: this.PublicHelpers.MissingFields.TaxNumber ? this.ProfileCompletion.TaxNumber : null,
            }, loadComplete);
        },

        // Todo: make function Promise
        async SendPinAgain(phoneNumber: string | null = null) {
            this.PublicHelpers.ServerError = '';
            
            const serverComplete = (Message: any): void => {
                if (Message.Success) {
                    this.PublicHelpers.CodeSent = true;
                } else {
                    this.PublicHelpers.ServerError = Message.Error;
                }
            };

            commClient.executeMethod("SendPin", {
                PhoneNumber: this.isUsernameNumber && phoneNumber == null ? this.Login.Username!.replace(/[\s+]+/g, '').trim() : phoneNumber,
                Email: !this.isUsernameNumber && phoneNumber == null ? this.Login.Username!.trim() : null
            }, serverComplete);

        },
        // UserCheckTaxNumber
        async CheckPosValidation() {
            const serverComplete = (Message: any): void => {
                if (Message.Success) {
                    router.push({name: 'registration-code'})
                    this.PublicHelpers.DataCheck = false;
                }
                else{
                    if(Message.Error){
                        this.PublicHelpers.ServerError = Message.Error
                    }
                    else{
                        this.PublicHelpers.DataCheck = true;    
                    }
                }
            };
            commClient.executeMethod("UserPOSValidation", {
                "TaxNumber": this.Registration.TaxNumber,
                "Msisdn": this.Registration.Prefix?.Key.toString().replace("+", "").trim() + this.Registration.Msisdn!.toString(),
                "Email": this.Registration.Email,

            }, serverComplete);
        },
        // UserCheckTaxNumber
        async CheckTaxNumber() {
            const serverComplete = (Message: any): void => {
                this.PublicHelpers.NextStepDisabled = false;

                if (Message) {
                    if(Message.Status == "OK"){
                        this.PublicHelpers.UserType = Message.UserType;
                    }
                    else{
                        this.PublicHelpers.ServerError = "Pri preverjanju davčne številke smo naleteli na težave.";
                    }
                } else {
                    this.PublicHelpers.UserType = 'X';
                }
            };

            let msisdn: string | null = null;

            if(this.Registration.Msisdn){
                msisdn = this.Registration.Prefix?.Key.toString().replace("+", "").trim() + this.Registration.Msisdn!.toString();
            }

            commClient.executeMethod("sp.UserCheckTaxNumber", {
                "TaxNumber": this.Registration.TaxNumber,
                Msisdn: msisdn
            }, serverComplete);
        },

        async passwordRequestDataCheck() {
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        router.push({ name: "email_verification" });
                    } else {
                        this.PublicHelpers.ServerError = Message.Error;
                    }
                }
            };

            commClient.executeMethod("PasswordRequestDataCheck", {
                "PhoneNumber": this.PasswordReset.Prefix?.Key.toString().replace("+", "").trim() + this.PasswordReset.Msisdn!.toString(),
                "Code": this.PasswordReset.ValidationCode,
                "TaxNumber": this.PasswordReset.TaxNumber,
                "Email": this.PasswordReset.Email
            }, serverComplete);
        },

        async passwordRequestDataCheckTRR() {
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        router.push({ name: "repeat-password", params: { passwordChangeRequestId: Message.passwordChangeRequestId }  }); // Message.passwordChangeRequestId
                    } else {
                        this.PublicHelpers.ServerError = Message.Error;
                    }
                }
            };

            commClient.executeMethod("PasswordRequestDataCheckTRR", {
                "PhoneNumber": this.PasswordReset.Prefix?.Key.toString().replace("+", "").trim() + this.PasswordReset.Msisdn!.toString(),
                "Code": this.PasswordReset.ValidationCode,
                "TaxNumber": this.PasswordReset.TaxNumber,
                "TRR": this.PasswordReset.TRR4Numbers
            }, serverComplete);
        },

        async passwordChange(passwordChangeRequestId: string) {
            const serverComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success) {
                        router.push({ name: "app_home" });
                    } else {
                        this.PublicHelpers.ServerError = Message.Error
                    }
                }
            };

            commClient.executeMethod("PortalPasswordChange", {
                "PasswordChangeRequestId": passwordChangeRequestId,
                "Password": this.PasswordReset.NewPassword
            }, serverComplete);
        },

        async choseCountryPhone() {
            const loadComplete = (Message: any): void => {
                if (Message) {
                    this.CountryPhoneList = Message as IKeyValue[];
                }
            };

            commClient.executeMethod("sf.CountryPhoneList", {}, loadComplete);
        },

        // Todo Show 120s error if we will decide to use this technique
        async RegisterUser(captchaToken: string) {
            const registerComplete = (Message: any): void => {
                if (Message.Success) {
                    router.push({ name: "registration-code" })
                }
                else{
                    this.PublicHelpers.ServerError = Message.Error;
                }
            }

            commClient.executeMethod("Register", {
                ...this.Registration,
                Msisdn: this.Registration.Prefix?.Key.toString().replace("+", "").trim() + this.Registration.Msisdn!.toString(),
                CaptchaToken: captchaToken
            }, registerComplete);
        },

        //password/registration
        async SetRegistrationPassword() {
            this.PublicHelpers.ServerError = '';
            const registerComplete = (Message: any): void => {
                if (Message) {
                    if (Message.Success == true) {
                        //nastavi authtoken
                        router.push({ name: "registration-success" })

                    }
                    else {
                        this.PublicHelpers.ServerError = "TAXNUM_NOT_FOUND";
                    }
                }
            }

            commClient.executeMethod("SetRegistrationPassword", {
                "TaxNumber": this.Registration.TaxNumber,
                "Msisdn": this.Registration.Prefix?.Key.toString().replace("+", "").trim() + this.Registration.Msisdn!.toString(),
                "Password": this.Registration.Password,
                "Code": this.Registration.ValidationCode,
            }, registerComplete);
        },


        CheckDavcna(numDavcna: number) {
            let davcna = numDavcna.toString();
            //preveri ali je številka
            if (!/^\d+$/.test(davcna))
                return false;
            if (davcna[0] != "0") {
                if (davcna.length == 8) {
                    var contr = parseInt(davcna[0]) * 8 +
                        parseInt(davcna[1]) * 7 +
                        parseInt(davcna[2]) * 6 +
                        parseInt(davcna[3]) * 5 +
                        parseInt(davcna[4]) * 4 +
                        parseInt(davcna[5]) * 3 +
                        parseInt(davcna[6]) * 2;
                    contr = contr % 11;
                    contr = (11 - contr) % 10;
                    if (parseInt(davcna[7]) == contr) {
                        this.PublicHelpers.CorrectDavcna = true;
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            } else {
                return false;
            }
            
        },

        toSignIn() {
            router.push({ name: 'public' })
        },

        toRegistration() {
            router.push({ name: 'register-start' })
        },

        toPasswordChange() {
            router.push({ name: 'reset-phone' })
        },

        toResetDetails() {
            router.push({ name: 'reset-details' })
        },

        toRepeatPassword() {
            router.push({ name: 'repeat-password' })
        },

        SetUsername(value: string){
            this.Login.Username = value.trim();
        }
    },
    getters: {
        isUsernameNumber(): boolean{
            if(this.Login.Username){
                const number = Number(this.Login.Username.replace(/[\s+]+/g, ''));
                return !isNaN(number) && isFinite(number);
            }

            return false;
        }
    }
});